import { getApiConfig } from '../config/markets.js';

const API_URL = getApiConfig().apiUrl;

/**
 * Adjuntos de los mensajes, servidos por waserver con la sesión del agente
 * (GET /messages/:conversationId/media/:messageId) en vez de abrir la URL
 * pública del bucket. El fichero se baja con el token y se pinta desde un
 * blob: URL local.
 */

// Lo que el navegador puede abrir en una pestaña sin ejecutar nada. Un blob: URL
// hereda el origen del inbox: un .html o .svg que mande el cliente NO se abre
// nunca, se descarga.
const INLINE_SAFE_TYPES = /^(image\/(jpeg|png|gif|webp)|audio\/|video\/|application\/pdf$)/;

// messageId -> { conversationId, promise, keep }
const cache = new Map();

async function fetchMedia(conversationId, messageId) {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('No se encontró token de autenticación');
  }

  const res = await fetch(`${API_URL}/messages/${conversationId}/media/${messageId}`, {
    headers: { 'Authorization': `Bearer ${token}` },
  });
  if (!res.ok) {
    throw new Error(`No se pudo obtener el adjunto (${res.status})`);
  }

  const blob = await res.blob();
  return { url: URL.createObjectURL(blob), type: (blob.type || '').toLowerCase() };
}

/**
 * Baja (una sola vez por mensaje) el adjunto y devuelve su blob: URL y su tipo.
 */
export function loadMessageMedia(conversationId, messageId) {
  let entry = cache.get(messageId);
  if (!entry) {
    entry = { conversationId, keep: false, promise: null };
    entry.promise = fetchMedia(conversationId, messageId).catch((err) => {
      cache.delete(messageId);
      throw err;
    });
    cache.set(messageId, entry);
  }
  return entry.promise;
}

/**
 * Abre el adjunto: en pestaña nueva si el tipo es seguro, como descarga si no.
 */
export async function openMessageMedia(conversationId, messageId, filename) {
  const media = await loadMessageMedia(conversationId, messageId);

  if (INLINE_SAFE_TYPES.test(media.type)) {
    // La pestaña sigue usando el blob: no se libera al cambiar de conversación.
    const entry = cache.get(messageId);
    if (entry) entry.keep = true;
    window.open(media.url, '_blank', 'noopener');
    return;
  }

  const link = document.createElement('a');
  link.href = media.url;
  link.download = filename || 'archivo';
  document.body.appendChild(link);
  link.click();
  link.remove();
}

/**
 * Libera los blobs de una conversación al salir de ella (menos los que siguen
 * abiertos en otra pestaña).
 */
export function releaseConversationMedia(conversationId) {
  for (const [messageId, entry] of cache) {
    if (entry.conversationId !== conversationId || entry.keep) continue;
    cache.delete(messageId);
    entry.promise.then((media) => URL.revokeObjectURL(media.url)).catch(() => {});
  }
}
